import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Upload, X, LogOut, Pencil, Ticket, Image as ImageIcon, ClipboardList, Loader2, Star } from "lucide-react";
import { useAdminAuth } from "../../context/AdminAuth";
import { api, resolveImg } from "../../lib/api";

const emptyEvent = () => ({
  title: "", date: "", venue: "", description: "", image_url: "",
  tiers: [{ name: "General Admission", price: 25, quantity: 100 }],
  featured: false, sold_out: false, published: true,
});

const field = "w-full bg-transparent border border-white/15 rounded-sm px-4 py-2.5 text-white focus:border-gold focus:outline-none";

function ImagePicker({ value, onChange }) {
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-4">
      {value ? (
        <img src={resolveImg(value)} alt="" className="w-24 h-24 object-cover rounded-sm border border-white/15" />
      ) : (
        <div className="w-24 h-24 rounded-sm border border-dashed border-white/20 flex items-center justify-center text-white/30"><ImageIcon size={22} /></div>
      )}
      <input ref={ref} type="file" accept="image/*" onChange={pick} className="hidden" data-testid="event-image-input" />
      <button type="button" onClick={() => ref.current.click()} disabled={busy} data-testid="event-image-upload"
        className="inline-flex items-center gap-2 px-4 py-2.5 border border-gold/50 text-gold text-xs uppercase tracking-[0.15em] rounded-sm hover:bg-gold hover:text-black transition-colors">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Upload size={15} />} {value ? "Replace" : "Upload"}
      </button>
    </div>
  );
}

function EventForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(initial || emptyEvent());
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setTier = (i, k, v) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const addTier = () => setForm((f) => ({ ...f, tiers: [...f.tiers, { name: "", price: 0, quantity: 50 }] }));
  const rmTier = (i) => setForm((f) => ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));

  const save = async () => {
    if (!form.title || !form.date || !form.venue) {
      toast.error("Title, date and venue are required.");
      return;
    }
    setSaving(true);
    const payload = {
      ...form,
      tiers: form.tiers.map((t) => ({ ...t, price: Number(t.price), quantity: Number(t.quantity) })),
    };
    try {
      if (form.id) await api.put(`/admin/events/${form.id}`, payload);
      else await api.post("/admin/events", payload);
      toast.success("Event saved");
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto p-4" data-testid="event-form-modal">
      <div className="w-full max-w-2xl bg-ink border border-gold/25 rounded-sm my-8">
        <div className="flex items-center justify-between px-7 py-5 border-b border-white/10">
          <h3 className="font-serif text-2xl font-light">{form.id ? "Edit Event" : "New Event"}</h3>
          <button onClick={onCancel} className="text-white/50 hover:text-gold"><X size={22} /></button>
        </div>
        <div className="p-7 space-y-5">
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Title</label>
            <input className={field} value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="event-title" />
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Date / Time</label>
              <input className={field} value={form.date} onChange={(e) => set("date", e.target.value)} placeholder="Dec 19, 2026 · 7:00 PM" data-testid="event-date" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Venue</label>
              <input className={field} value={form.venue} onChange={(e) => set("venue", e.target.value)} data-testid="event-venue" />
            </div>
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Description</label>
            <textarea rows={3} className={`${field} resize-none`} value={form.description} onChange={(e) => set("description", e.target.value)} data-testid="event-description" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-[0.2em] text-white/40 block mb-2">Poster Image</label>
            <ImagePicker value={form.image_url} onChange={(url) => set("image_url", url)} />
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[11px] uppercase tracking-[0.2em] text-white/40">Ticket Tiers</label>
              <button type="button" onClick={addTier} className="text-gold text-xs inline-flex items-center gap-1 hover:text-gold-light" data-testid="add-tier"><Plus size={14} /> Add tier</button>
            </div>
            <div className="space-y-3">
              {form.tiers.map((t, i) => (
                <div key={i} className="flex items-center gap-3" data-testid={`tier-row-${i}`}>
                  <input className={`${field} flex-1`} placeholder="Name (e.g. VIP)" value={t.name} onChange={(e) => setTier(i, "name", e.target.value)} data-testid={`tier-name-${i}`} />
                  <div className="relative w-28">
                    <span className="absolute left-3 top-2.5 text-white/40">$</span>
                    <input type="number" min="0" className={`${field} pl-7`} placeholder="Price" value={t.price} onChange={(e) => setTier(i, "price", e.target.value)} data-testid={`tier-price-${i}`} />
                  </div>
                  <input type="number" min="0" className={`${field} w-24`} placeholder="Qty" value={t.quantity} onChange={(e) => setTier(i, "quantity", e.target.value)} data-testid={`tier-qty-${i}`} />
                  {form.tiers.length > 1 && <button type="button" onClick={() => rmTier(i)} className="text-white/40 hover:text-red-400"><Trash2 size={16} /></button>}
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap gap-6 pt-2">
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.featured} onChange={(e) => set("featured", e.target.checked)} data-testid="event-featured" className="accent-[#D4AF37]" /> Featured
            </label>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.sold_out} onChange={(e) => set("sold_out", e.target.checked)} data-testid="event-soldout" className="accent-[#D4AF37]" /> Sold Out
            </label>
            <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => set("published", e.target.checked)} data-testid="event-published" className="accent-[#D4AF37]" /> Published
            </label>
          </div>
        </div>
        <div className="flex justify-end gap-3 px-7 py-5 border-t border-white/10">
          <button onClick={onCancel} className="px-6 py-3 border border-white/20 text-white/70 text-xs uppercase tracking-[0.2em] rounded-sm hover:border-white/40">Cancel</button>
          <button onClick={save} disabled={saving} data-testid="event-save"
            className="px-8 py-3 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light disabled:opacity-50">{saving ? "Saving…" : "Save Event"}</button>
        </div>
      </div>
    </div>
  );
}

function EventsTab() {
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null); // event obj or "new"
  const load = () => api.get("/admin/events").then((r) => setEvents(r.data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    await api.delete(`/admin/events/${id}`);
    toast.success("Event deleted");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl font-light">Events</h2>
        <button onClick={() => setEditing("new")} data-testid="new-event-button"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light"><Plus size={16} /> New Event</button>
      </div>
      {events.length === 0 && <p className="text-white/40">No events yet. Create your first one.</p>}
      <div className="space-y-3">
        {events.map((ev) => (
          <div key={ev.id} data-testid={`admin-event-${ev.id}`} className="flex items-center gap-4 p-4 border border-white/10 rounded-sm bg-[#0b0b0b]">
            <div className="w-16 h-16 shrink-0 rounded-sm overflow-hidden bg-white/5 flex items-center justify-center">
              {ev.image_url ? <img src={resolveImg(ev.image_url)} alt="" className="w-full h-full object-cover" /> : <Ticket className="text-white/20" size={20} />}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <p className="font-serif text-lg truncate">{ev.title}</p>
                {ev.featured && <Star size={14} className="text-gold shrink-0" />}
                {ev.sold_out && <span className="text-[10px] uppercase tracking-wide text-red-400 border border-red-400/40 px-1.5 rounded">Sold out</span>}
                {!ev.published && <span className="text-[10px] uppercase tracking-wide text-white/40 border border-white/20 px-1.5 rounded">Draft</span>}
              </div>
              <p className="text-white/40 text-sm truncate">{ev.date} · {ev.venue}</p>
              <p className="text-white/50 text-xs mt-1">{ev.tiers.map((t) => `${t.name} $${t.price} (${t.remaining}/${t.quantity})`).join("  ·  ")}</p>
            </div>
            <button onClick={() => setEditing(ev)} data-testid={`edit-event-${ev.id}`} className="text-white/50 hover:text-gold p-2"><Pencil size={17} /></button>
            <button onClick={() => remove(ev.id)} data-testid={`delete-event-${ev.id}`} className="text-white/50 hover:text-red-400 p-2"><Trash2 size={17} /></button>
          </div>
        ))}
      </div>
      {editing && (
        <EventForm
          initial={editing === "new" ? null : editing}
          onSaved={() => { setEditing(null); load(); }}
          onCancel={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function GalleryTab() {
  const [items, setItems] = useState([]);
  const ref = useRef();
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/gallery").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.post("/admin/gallery", { image_url: data.url, caption: "" });
      toast.success("Photo added to gallery");
      load();
    } catch {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = "";
    }
  };

  const remove = async (id) => {
    await api.delete(`/admin/gallery/${id}`);
    toast.success("Photo removed");
    load();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-serif text-2xl font-light">Photo Gallery</h2>
        <input ref={ref} type="file" accept="image/*" onChange={add} className="hidden" data-testid="gallery-upload-input" />
        <button onClick={() => ref.current.click()} disabled={busy} data-testid="gallery-upload-button"
          className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-black text-xs uppercase tracking-[0.2em] rounded-sm hover:bg-gold-light disabled:opacity-50">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add Photo
        </button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
        {items.map((it) => (
          <div key={it.id} data-testid={`admin-gallery-${it.id}`} className="group relative aspect-square rounded-sm overflow-hidden border border-white/10">
            <img src={resolveImg(it.image_url)} alt="" className="w-full h-full object-cover" />
            <button onClick={() => remove(it.id)} data-testid={`delete-gallery-${it.id}`}
              className="absolute top-2 right-2 w-8 h-8 rounded-full bg-black/70 text-white/80 flex items-center justify-center opacity-0 group-hover:opacity-100 hover:text-red-400 transition-opacity"><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrdersTab() {
  const [orders, setOrders] = useState([]);
  useEffect(() => { api.get("/admin/orders").then((r) => setOrders(r.data)); }, []);
  const revenue = orders.reduce((s, o) => s + Number(o.amount || 0), 0);
  return (
    <div>
      <div className="flex flex-wrap gap-6 mb-8">
        <div className="border border-white/10 rounded-sm px-6 py-4 bg-[#0b0b0b]">
          <div className="text-white/40 text-xs uppercase tracking-[0.2em]">Tickets Sold</div>
          <div className="font-serif text-3xl text-gold mt-1">{orders.reduce((s, o) => s + (o.quantity || 0), 0)}</div>
        </div>
        <div className="border border-white/10 rounded-sm px-6 py-4 bg-[#0b0b0b]">
          <div className="text-white/40 text-xs uppercase tracking-[0.2em]">Revenue</div>
          <div className="font-serif text-3xl text-gold mt-1">${revenue.toFixed(2)}</div>
        </div>
      </div>
      {orders.length === 0 && <p className="text-white/40">No paid orders yet.</p>}
      <div className="space-y-2">
        {orders.map((o) => (
          <div key={o.session_id} className="flex items-center justify-between gap-4 p-4 border border-white/10 rounded-sm bg-[#0b0b0b] text-sm">
            <div className="min-w-0">
              <p className="text-white truncate">{o.buyer_name} <span className="text-white/40">· {o.buyer_email}</span></p>
              <p className="text-white/50 truncate">{o.event_title} — {o.quantity} × {o.tier_name}</p>
            </div>
            <div className="text-gold font-serif text-lg shrink-0">${Number(o.amount).toFixed(2)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { admin, logout } = useAdminAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("events");

  const tabs = [
    { id: "events", label: "Events", icon: Ticket },
    { id: "gallery", label: "Gallery", icon: ImageIcon },
    { id: "orders", label: "Orders", icon: ClipboardList },
  ];

  return (
    <div className="min-h-screen bg-ink text-white">
      <header className="border-b border-white/10 sticky top-0 z-40 bg-ink/90 backdrop-blur">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/mc-logo.png" alt="" className="h-9 w-9 object-contain" />
            <span className="font-serif text-gold">Admin Portal</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" className="text-white/50 hover:text-gold text-xs uppercase tracking-[0.15em]">View Site</a>
            <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="admin-logout"
              className="inline-flex items-center gap-2 text-white/60 hover:text-gold text-xs uppercase tracking-[0.15em]"><LogOut size={15} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-10 border-b border-white/10">
          {tabs.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-5 py-3 text-sm uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
                tab === t.id ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white"}`}>
              <t.icon size={16} /> {t.label}
            </button>
          ))}
        </div>
        {tab === "events" && <EventsTab />}
        {tab === "gallery" && <GalleryTab />}
        {tab === "orders" && <OrdersTab />}
      </div>
    </div>
  );
}
